import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { brandingApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import { Alert } from '@/components/common/Ui'
import { useBranding } from '@/lib/useBranding'

/**
 * The two logos and the favicon, on Settings > Company.
 *
 * Two slots because the artwork is drawn for where it sits: the portal's on
 * the green sidebar, the order sheet's on white paper. Each preview is drawn
 * on that background, so a dark logo that would vanish on the sidebar is
 * seen to vanish before anyone else sees it.
 *
 * An upload saves at once rather than waiting for the Save button below —
 * that button sends text fields, and a file is not one.
 */
const SLOTS = [
  {
    kind: 'site',
    label: 'Portal logo',
    help: 'Shown on the green sidebar, the login page and the customer order page. Use light artwork on a transparent background (PNG).',
    // The sidebar draws it 172px wide; twice that stays sharp on a
    // high-density screen. A tall logo comes out tiny at that width.
    size: 'Best size: a wide logo, about 540 × 120 px (roughly 4½ : 1). At least 350 px wide. Under 5 MB.',
    previewClass: 'logo-preview logo-preview-dark',
  },
  {
    kind: 'invoice',
    label: 'Invoice logo',
    help: 'Printed at the top of every invoice sheet (PDF), on white paper. PNG or JPG.',
    // Printed 28 mm wide; 600px is print-sharp at that width.
    size: 'Best size: about 900 × 260 px (roughly 3½ : 1). At least 600 px wide. Under 5 MB.',
    previewClass: 'logo-preview',
  },
  {
    kind: 'favicon',
    label: 'Favicon',
    help: 'The small icon in the browser tab and bookmarks, for the portal and the customer order page. A simple mark reads best — just the symbol, not the full name.',
    // Shown at 16–48 px; the server makes it a 256 px square either way.
    size: 'Best size: a square image, at least 256 × 256 px (PNG). A wide image is centred on a transparent square. Under 5 MB.',
    previewClass: 'logo-preview favicon-preview',
    button: 'Upload new favicon',
  },
]

export default function LogoSettings({ canEdit }) {
  const { data, siteLogo, invoiceLogo, favicon } = useBranding()
  const sources = { site: siteLogo, invoice: invoiceLogo, favicon }
  return (
    <>
      {SLOTS.map((slot) => (
        <LogoSlot
          key={slot.kind}
          slot={slot}
          canEdit={canEdit}
          src={sources[slot.kind]}
          custom={
            slot.kind === 'favicon' ? Boolean(data?.favicon) : Boolean(data?.[`${slot.kind}_custom`])
          }
        />
      ))}
    </>
  )
}

function LogoSlot({ slot, canEdit, src, custom }) {
  const queryClient = useQueryClient()
  const input = useRef(null)
  const [error, setError] = useState('')

  const done = {
    onSuccess: (fresh) => {
      queryClient.setQueryData(['branding'], fresh)
      setError('')
    },
    onError: (err) => setError(errorMessage(err)),
  }
  const upload = useMutation({
    mutationFn: (file) => brandingApi.upload(slot.kind, file),
    ...done,
  })
  const reset = useMutation({
    mutationFn: () => brandingApi.reset(slot.kind),
    ...done,
  })
  const busy = upload.isPending || reset.isPending

  return (
    <section className="panel settings-row">
      <div className="settings-label">
        {/* A title, not a <label>: a label tied to the file input opened
            the file picker whenever someone clicked the heading. */}
        <span className="settings-title">{slot.label}</span>
        {custom && (
          <span className="tag" title="Uploaded here, replacing the built-in logo">
            changed
          </span>
        )}
      </div>
      <p className="hint">{slot.help}</p>
      <p className="hint">{slot.size}</p>
      <Alert tone="danger" message={error} onClose={() => setError('')} />

      <div className="settings-control logo-control">
        <div className={slot.previewClass}>
          {src ? <img src={src} alt={slot.label} /> : <span className="hint">No logo</span>}
        </div>

        {canEdit && (
          <div className="logo-actions">
            <input
              ref={input}
              id={`logo-${slot.kind}`}
              aria-label={slot.label}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0]
                // Cleared so choosing the same file again still uploads.
                e.target.value = ''
                if (file) upload.mutate(file)
              }}
            />
            <button
              type="button"
              className="btn btn-sm"
              disabled={busy}
              onClick={() => input.current?.click()}
            >
              {upload.isPending ? 'Uploading…' : slot.button ?? 'Upload new logo'}
            </button>
            {custom && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={busy}
                onClick={() => reset.mutate()}
              >
                {reset.isPending ? 'Resetting…' : 'Use the default'}
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
